import { addToDownloadQueue } from "../providers/FileDownloader";
import { Document } from "../db";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Checkbox,
  FormControlLabel,
  Typography,
  Box,
  Divider,
} from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import prettyBytes from "pretty-bytes";
import { getAvailableStorageSpace } from "../providers/Storage";

export default function ConfirmDownloadDialog(props: { docs: Document[]; onClose: () => void }) {
  const { docs, onClose } = props;
  const [redownload, setRedownload] = useState(false);
  const [availableSpace, setAvailableSpace] = useState<number>(0);

  // Reset redownload when docs change
  useEffect(() => {
    setRedownload(false);
  }, [docs]);

  // Fetch storage estimate
  useEffect(() => {
    async function getStorage() {
      try {
        const availableSpace = await getAvailableStorageSpace();
        setAvailableSpace(availableSpace!);
      } catch (error) {
        console.log(error);
      }
    }
    getStorage();
  }, []);

  const filtered = useMemo(
    () => (redownload ? docs : docs.filter((d) => !d.stored_etag || d.stored_etag !== d.etag)),
    [docs, redownload]
  );

  function confirmDownload() {
    addToDownloadQueue(filtered);
    onClose();
  }

  const size = useMemo(() => {
    return filtered.reduce((acc, d) => acc + d.size, 0);
  }, [filtered]);

  return (
    <Dialog onClose={onClose} open={docs.length > 0} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Typography variant="h6" align="center">
          Confirm Download
        </Typography>
      </DialogTitle>
      <Divider />
      <DialogContent>
        <Box my={2}>
          {filtered.length === 0 ? (
            <Typography variant="body1" align="center">
              All files in this view have already been downloaded. Do you want to download them again?
            </Typography>
          ) : (
            <>
              <Typography variant="body1" align="center">
                Do you want to download {filtered.length} documents ({prettyBytes(size)})?
              </Typography>
              <Typography variant="body2" color="textSecondary" align="center">
                Files will only download while this app is visible on the screen. Downloading large numbers of files may take some
                time.
              </Typography>

              {/* Distinct storage info */}
              <Typography variant="body1" align="center" color="secondary" fontWeight="bold" mt={2}>
                Available Storage: {prettyBytes(availableSpace)}
              </Typography>
            </>
          )}
        </Box>
        <Box display="flex" justifyContent="center" my={1}>
          <FormControlLabel
            control={<Checkbox checked={redownload} onChange={(evt) => setRedownload(evt.target.checked)} />}
            label="Download files that have already been downloaded again"
          />
        </Box>
      </DialogContent>
      <Divider />
      <DialogActions>
        <Button onClick={onClose} autoFocus color="secondary" variant="outlined">
          No
        </Button>
        <Button onClick={confirmDownload} color="primary" variant="contained" disabled={filtered.length === 0}>
          Yes
        </Button>
      </DialogActions>
    </Dialog>
  );
}
